import { Link, useLocation } from 'react-router-dom';
import { Menu } from 'lucide-react';
import { useUIStore } from '@/store';
import { cn } from '@core/utils';
import { useRoleBasedMenu } from '@/hooks/use-role-based-menu';
import type { MenuItem } from '@/config/menu';
import { getSidebarMenuIcon } from './menu-icon-map';
import './mobile-tab-bar.css';

/**
 * The phone tab bar's destinations, in priority order: Home, then the first three of the rest
 * that the user's own menu contains. Looked up in `useRoleBasedMenu`, never added to it, so a
 * tab cannot offer a page the user's plan or grants leave out (decision P9,
 * `docs/platform/PHASES.md`). `match` is the path prefix that marks the tab current.
 */
const TABS: { path: string; label: string; match: string }[] = [
  { path: '/home', label: 'Home', match: '/home' },
  { path: '/team/prospect-tracker', label: 'Prospects', match: '/team/prospect-tracker' },
  { path: '/bpm/overview', label: 'BPM', match: '/bpm' },
  { path: '/matchup', label: 'Matchup', match: '/matchup' },
  { path: '/calendar', label: 'Calendar', match: '/calendar' },
  { path: '/training-center', label: 'Training', match: '/training-center' },
];
const MAX_TABS = 4;

function findByPath(items: MenuItem[], path: string): MenuItem | null {
  for (const item of items) {
    if (item.path === path) return item;
    const found = item.children ? findByPath(item.children, path) : null;
    if (found) return found;
  }
  return null;
}

/** Bottom navigation for phones (`MainLayout` mounts it only there). The last tab opens the full menu. */
export function MobileTabBar() {
  const menuItems = useRoleBasedMenu();
  const { pathname } = useLocation();
  const mobileNavOpen = useUIStore((state) => state.mobileNavOpen);
  const toggleMobileNav = useUIStore((state) => state.toggleMobileNav);

  const tabs = TABS.flatMap((tab) => {
    const item = findByPath(menuItems, tab.path);
    return item ? [{ ...tab, item }] : [];
  }).slice(0, MAX_TABS);
  // Something in the menu needs action (helpdesk queue, feature board): say so on Menu.
  const menuNeedsAction = menuItems.some((item) => (item.badge ?? 0) > 0);

  return (
    <nav className="wb-tabbar" aria-label="Quick navigation">
      {tabs.map(({ path, label, match, item }) => {
        const current = pathname === match || pathname.startsWith(`${match}/`);
        return (
          // `Link`, not `NavLink`: NavLink sets `aria-current` from its own exact match, which
          // would miss a sub-page such as /bpm/guest-checkin under the BPM tab.
          <Link
            key={path}
            to={path}
            className={cn('wb-tabbar__tab', current && 'wb-tabbar__tab--current')}
            aria-current={current ? 'page' : undefined}
          >
            <span className="wb-tabbar__icon" aria-hidden="true">
              {getSidebarMenuIcon(item.label, item.icon, 22)}
            </span>
            <span className="wb-tabbar__label">{label}</span>
          </Link>
        );
      })}
      <button
        type="button"
        className={cn('wb-tabbar__tab', mobileNavOpen && 'wb-tabbar__tab--current')}
        onClick={toggleMobileNav}
        aria-controls="app-sidebar"
        aria-expanded={mobileNavOpen}
      >
        <span className="wb-tabbar__icon" aria-hidden="true">
          <Menu size={22} />
          {menuNeedsAction && <span className="wb-tabbar__dot" />}
        </span>
        <span className="wb-tabbar__label">Menu</span>
        {menuNeedsAction && <span className="sr-only">, items need action</span>}
      </button>
    </nav>
  );
}
